/* ============================================================
 * DESKTOP FILTER CARD — Leads + Daily Request (owner 2026-11-04)
 * ------------------------------------------------------------
 * On a desktop width the two pages' filter area is ONE white card with three
 * rows that all start at the same left edge:
 *   a. the status chips (compact pills), a thin divider under them;
 *   b. "Date Added" + the shared period chips, a 1px vertical divider, then
 *      the page's toggles;
 *   c. "Filter by" + the dropdowns as compact chip-styled selects.
 * Rows b and c share ONE label column (FILTER_LABEL_W). The page's action
 * buttons leave the filter area and portal into the header row, beside the
 * search box — REVERSED 2026-11-04 (second pass): they live in ActionBar, the ONE slim bar between the card and
 * the table (selection actions left while rows are ticked, the page's standard buttons right); the header is title + search.
 *
 * PRESENTATION ONLY, like MobileFilterChrome.js: no filter state, no rule. The
 * pages hand in the SAME nodes they already build (same setters, counts, role
 * gates). Phones never mount any of this. verify-mobile-filter-chrome.js §DESK
 * runs it against the pages' real desktop branches.
 * ============================================================ */
import React, { cloneElement, isValidElement } from "react";

export var FILTER_LABEL_W = 72;
var LINE = "#E8ECF1", MUTED = "#64748B", INK = "#1E293B";

// Status chips on desktop: a compact pill. Merged over the chip's own style (colours stay the page's).
export var DESKTOP_STATUS_PILL = { padding: "6px 11px", fontSize: 11.5, borderRadius: 999 };
// Row-b toggles take the period chip's box (height / padding / radius / size) so the row is one height.
export var DESKTOP_TOGGLE_BOX = { boxSizing: "border-box", height: 30, minHeight: 30, maxHeight: 30, padding: "0 12px", borderRadius: 8, fontSize: 12, whiteSpace: "nowrap" };
// Row-c dropdowns: SearchableSelect's trigger button in the same box (caret stays inside, on the right).
export var FILTER_SELECT_CHIP = { boxSizing: "border-box", height: 30, minHeight: 30, maxHeight: 30, padding: "0 10px 0 12px", borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 12, fontWeight: 600, color: MUTED, gap: 8, whiteSpace: "nowrap" };
export var FILTER_SELECT_MAX_W = 280;

// "⏱ Last Activity" -> "Last Activity" (desktop selects carry no leading emoji).
export function stripLeadingEmoji(l) { return String(l == null ? "" : l).replace(/^(?:\p{Extended_Pictographic}|️|‍|\s)+/u, ""); }
export function stripOptionEmoji(opts) { return (opts || []).map(function (o) { return { value: o.value, label: stripLeadingEmoji(o.label) }; }); }

// Re-order an actions node's children for the bar's right side: `order` lists child indexes first; any index it does not name
// and `elsewhere` does not claim follows in its original place-order, so a button added later can never silently vanish.
// Falsy children (role-gated off) stay falsy. Keys are added because the result is a new array.
export function orderChildren(kids, order, elsewhere) {
  var list = Array.isArray(kids) ? kids : [kids], seen = {}, out = [];
  (elsewhere || []).forEach(function (i) { seen[i] = 1; });
  order.concat(list.map(function (_, i) { return i; })).forEach(function (i) {
    if (seen[i] || i >= list.length) return; seen[i] = 1;
    var el = list[i];
    out.push(isValidElement(el) ? cloneElement(el, { key: "hdr" + i }) : (el || null));
  });
  return out;
}
// Exactly the named children, in that order (the selection-only buttons).
export function pickChildren(kids, order) {
  var list = Array.isArray(kids) ? kids : [kids];
  return order.map(function (i) { var el = list[i]; return isValidElement(el) ? cloneElement(el, { key: "sel" + i }) : (el || null); });
}

// The page's DateFilterBar, minus its own leading label — row b's label column carries it instead.
export function withoutLabel(el) { return isValidElement(el) ? cloneElement(el, { label: undefined }) : el; }

// ===== THE JOINED PANEL (owner 2026-11-05) =====
// Filters → action bar → table are ONE white card: 1px border, radius 14, no gap between the three. The joins are 1px
// #F1F2F4 lines: under the filters block always, under the action bar when it renders — so with the bar hidden (sales on
// Leads, idle) the table still sits under exactly one divider.
// NOT overflow:hidden on the panel, deliberately: the 📅 calendar and the Quarter picker are absolutely positioned inside the
// filters block and run ~300px below it — over the table — so a short table (no rows, a few rows) would cut them off
// (measured, harness). The rounded bottom corners are clipped where the old table card clipped them: the table section.
export var JOIN_LINE = "1px solid #F1F2F4";
export var JOINED_PANEL_STYLE = { background: "#fff", border: "1px solid " + LINE, borderRadius: 14, boxShadow: "0 1px 4px rgba(0,0,0,0.07)", overflow: "visible", position: "relative" };
// The table's own card inside the panel: no border / shadow, square top flush under the bar, the panel's inner radius (13) at the bottom.
export var JOINED_TABLE = { border: "none", boxShadow: "none", borderRadius: "0 0 13px 13px", overflow: "hidden" };
export var BAR_BG = "#FAFBFC", BAR_BG_SELECTED = "#FFFBEB";
export function JoinedPanel(p) {
  if (!p.on) return <>{p.children}</>;
  return <div data-joined-panel={p.page || "1"} style={JOINED_PANEL_STYLE}>{p.children}</div>;
}

export function FilterCard(p) {
  // the filters block of the joined panel: padding 12/14, the panel's background, the 1px join line under it
  return <div data-filter-card={p.page || "1"} style={{ position: "relative", zIndex: 90, padding: "12px 14px", borderBottom: JOIN_LINE, borderRadius: "14px 14px 0 0", display: "flex", flexDirection: "column", gap: 10 }}>{p.children}</div>;
}

// Row a: the status chips, wrapping inside the card, with the thin divider under them.
export function FilterCardChips(p) {
  return <div data-filter-row="status" style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center", paddingBottom: 10, borderBottom: "1px solid " + LINE }}>{p.children}</div>;
}

// Rows b and c: the fixed label column, then the row's controls (wrapping under the controls, not the label).
export function FilterCardRow(p) {
  return <div data-filter-row={p.row} style={{ display: "flex", alignItems: "center", gap: 0 }}>
    <span data-filter-label="1" style={{ flex: "0 0 " + FILTER_LABEL_W + "px", width: FILTER_LABEL_W, fontSize: 11, fontWeight: 600, color: MUTED, whiteSpace: "nowrap" }}>{p.label}</span>
    <div style={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>{p.children}</div>
  </div>;
}

export function FilterDivider() {
  return <span aria-hidden="true" data-filter-divider="1" style={{ width: 1, height: 22, background: "#E2E8F0", margin: "0 4px", flexShrink: 0 }} />;
}

// Action-bar buttons are COMPACT on desktop (owner 2026-11-04, third pass): with the most selection buttons a role can
// see, the whole bar must stay on ONE line at 1280 and 1440. The page's own Btn nodes are cloned with this box and a
// shorter label; the phone FAB keeps reading the originals (its labels are unchanged).
// Height pinned (28) so an emoji label (💬 / 📡 glyphs are taller than the text) cannot make one button taller than its row.
export var ACTION_COMPACT = { padding: "6px 10px", fontSize: 11.5, borderRadius: 8, gap: 5, whiteSpace: "nowrap", flexShrink: 0, boxSizing: "border-box", height: 28, minHeight: 28, maxHeight: 28, lineHeight: 1 };
var SHORT_LABELS = [
  [/^Import Excel$/, "Import"], [/^Export Excel$/, "Export"],
  [/^Bulk Reassign \((\d+)\)$/, "Reassign ($1)"], [/^Archive \((\d+)\)$/, "Archive ($1)"],
  [/^(\S+ )?Change Source \((\d+)\)$/, "$1Source ($2)"], [/^(\S+ )?Bulk WhatsApp \((\d+)\)$/, "$1WhatsApp ($2)"]
];
export function shortActionLabel(text) {
  var t = String(text == null ? "" : text).replace(/\s+/g, " ").trim();
  for (var i = 0; i < SHORT_LABELS.length; i++) if (SHORT_LABELS[i][0].test(t)) return t.replace(SHORT_LABELS[i][0], SHORT_LABELS[i][1]);
  return t;
}
function flatKids(c) { return c == null || typeof c === "boolean" ? [] : Array.isArray(c) ? c.reduce(function (o, x) { return o.concat(flatKids(x)); }, []) : [c]; }
export function compactAction(el) {
  if (!isValidElement(el) || el.type === "input") return el;
  var parts = flatKids(el.props.children);
  var icons = parts.filter(isValidElement), text = parts.filter(function (x) { return !isValidElement(x); }).join("");
  return cloneElement.apply(null, [el, { style: Object.assign({}, el.props.style, ACTION_COMPACT) }].concat(icons, [shortActionLabel(text)]));
}

// THE one bar between the filter card and the table (owner 2026-11-04). Left: EMPTY when nothing is ticked (the row count
// was removed the same day — the All chip carries it); while rows are ticked, "N selected ·" and the selection-only
// actions. Right: the page's standard buttons, always. One bar in
// both states: the selection never opens a second one. ONE LINE (owner 2026-11-04): nothing in the bar wraps — the bar,
// both halves and every button are nowrap; compact buttons are what make the widest case fit at 1280 (measured, see gate).
export function ActionBar(p) {
  var sel = (p.selection || []).filter(Boolean).map(compactAction), acts = (p.actions || []).filter(Boolean).map(compactAction);
  // Nothing to show on either side (e.g. sales on Leads, nothing ticked): no bar at all — not an empty 34px strip (owner 2026-11-04).
  if (!(p.selectedCount > 0) && !acts.filter(function (el) { return !isValidElement(el) || el.type !== "input"; }).length) return null;
  return <div data-action-bar={p.page || "1"} data-selected={p.selectedCount > 0 ? "1" : "0"} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "nowrap", whiteSpace: "nowrap", padding: "8px 14px", minHeight: 28, borderBottom: JOIN_LINE, background: p.selectedCount > 0 ? BAR_BG_SELECTED : BAR_BG }}>
    <div data-action-bar-left="1" style={{ flex: "1 1 auto", display: "flex", alignItems: "center", gap: 6, flexWrap: "nowrap", whiteSpace: "nowrap", minWidth: 0 }}>
      {p.selectedCount > 0 && <span data-selected-count="1" style={{ fontSize: 13, fontWeight: 700, color: INK, whiteSpace: "nowrap" }}>{p.selectedCount + " selected" + (sel.length ? " \u00b7" : "")}</span>}
      {p.selectedCount > 0 && sel}
    </div>
    <div data-action-bar-right="1" style={{ flex: "0 0 auto", display: "flex", alignItems: "center", gap: 6, flexWrap: "nowrap", whiteSpace: "nowrap", justifyContent: "flex-end" }}>{acts}</div>
  </div>;
}
